import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';
import { Event, RsvpResponse } from '../types';
import toast from 'react-hot-toast';

export const useEvents = (nodeId: string, from?: string, to?: string) => {
  return useQuery<Event[]>({
    queryKey: ['events', nodeId, from, to],
    queryFn: () => {
      if (from || to) {
        return apiWorker.get(`/api/nodes/${nodeId}/calendar`, {
          params: { start: from, end: to }
        }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
      }
      return apiWorker.get(`/api/nodes/${nodeId}/events`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    enabled: !!nodeId,
  });
};

export const useCreateEvent = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Event>) => 
      apiWorker.post(`/api/nodes/${nodeId}/events`, data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', nodeId] });
      toast.success('Event created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create event');
    },
  });
};

export const useRsvp = (nodeId: string, eventId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (arg: RsvpResponse | { eventId: string; response: RsvpResponse }) => {
      const targetEventId = typeof arg === 'object' ? arg.eventId : eventId;
      const response = typeof arg === 'object' ? arg.response : arg;
      return apiWorker.post(`/api/nodes/${nodeId}/events/${targetEventId}/rsvp`, { response }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('RSVP updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update RSVP');
    },
  });
};

/**
 * Mutation to update an event.
 */
export const useUpdateEvent = (nodeId: string, eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Event>) =>
      apiWorker.put(`/api/nodes/${nodeId}/events/${eventId}`, data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('Event updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update event');
    },
  });
};

/**
 * Mutation to cancel an event.
 */
export const useCancelEvent = (nodeId: string, eventId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (overrideEventId?: string | void) => {
      const targetEventId = typeof overrideEventId === 'string' && overrideEventId ? overrideEventId : eventId;
      const endpoint = nodeId ? `/api/nodes/${nodeId}/events/${targetEventId}/cancel` : `/api/events/${targetEventId}/cancel`;
      return apiWorker.post(endpoint).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('Event cancelled');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to cancel event');
    },
  });
};
